import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { useCurrency } from '../../context/CurrencyContext';
import { F, MONO_FEAT } from '../../styles/theme';
import { fmtC } from '../../utils/format';
import ExchangeLogo from '../ExchangeLogo';
import { OverlayCloseBtn } from './OverlayHeader';

// ── Datencheck-Sheet ─────────────────────────────────────────────────────────
// Bottom-Sheet (fährt von unten hoch, einheitliche Overlay-Sprache via
// OverlayHeader). Zeigt den Vollständigkeits-/Vertrauens-Stand: offene Punkte
// als Inbox + die Quellen mit Import-Typ. Daten kommen fertig aus buildDataCheck.
// Calm-konform: Status-Punkte statt Icons, keine getönten Container, Rot nur als
// kleiner Akzent. Inhalt aus dem abgenommenen Mock `brand/completeness-calm.html`.

const Z = 340;

function fmtDateShort(d, locale) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// Eine Inbox-Zeile: Status-Punkt · Titel/Sub · Chevron-CTA.
function PointRow({ T, dot, title, sub, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mo-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: '15px 10px', margin: '0 -10px',
        background: 'transparent', border: 'none',
        borderBottom: `1px solid ${T.lineSoft || T.line}`,
        borderRadius: 11, cursor: 'pointer', textAlign: 'left', color: T.text,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: dot, flexShrink: 0, margin: '0 5px' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
        {sub && <span style={{ display: 'block', fontSize: 12, color: T.sub, marginTop: 2 }}>{sub}</span>}
      </span>
      <span className="mo-chev" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', color: T.sub }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </button>
  );
}

export default function DataCheckSheet({ open, onClose, data, onNavigate, onResolveFix }) {
  const { T } = useTheme();
  const { t } = useT();
  const { locale } = useCurrency();
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) { setMounted(true); return; }
    if (mounted) { const id = setTimeout(() => setMounted(false), 260); return () => clearTimeout(id); }
  }, [open, mounted]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted || !data) return null;

  const { openCount, points, dustCount, sources, connectedCount, totalSources } = data;
  const allClear = openCount === 0;

  // Quellen nach Basis-Börse gruppieren (z.B. „Bitget Futures" + „Bitget" unter
  // EINEM Bitget-Logo, Einträge untereinander). Reihenfolge = erste Erscheinung.
  const sourceGroups = (() => {
    const groups = [];
    const idx = new Map();
    for (const s of sources) {
      let g = idx.get(s.logoName);
      if (!g) { g = { logoName: s.logoName, entries: [] }; idx.set(s.logoName, g); groups.push(g); }
      g.entries.push(s);
    }
    return groups;
  })();

  // Unterzeile = Import-Typ + Anzahl (wie im Mock). On-Chain trägt seinen Zusatz
  // schon im Text. Rechts steht der Status, nicht der Typ.
  const srcDesc = (s) => s.kind === 'chain'
    ? t('dc_src_chain')
    : `${s.kind === 'api' ? t('dc_src_api') : s.kind === 'csv' ? t('dc_src_csv') : t('dc_src_manual')} · ${t('dc_tx_count', { n: s.count })}`;
  const srcStatus = (kind) => (kind === 'api' || kind === 'chain') ? t('dc_status_connected')
    : kind === 'csv' ? t('dc_status_imported') : t('dc_status_manual');
  const srcDot = (kind) => (kind === 'api' || kind === 'chain') ? (T.gain || T.green) : T.faint || T.mute;

  const go = (target) => { onNavigate?.(target); onClose?.(); };
  // Beide CTAs („Zuordnen"/„Ergänzen") → gemeinsame „Zu klären"-Seite; Fallback Wallet.
  const goFix = () => { if (onResolveFix) { onResolveFix(); onClose?.(); } else go('wallet'); };

  const sheet = (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: Z,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px) saturate(140%)', WebkitBackdropFilter: 'blur(8px) saturate(140%)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: `dcFade ${open ? 180 : 200}ms ease ${open ? 'forwards' : ''}`,
        opacity: open ? 1 : 0,
      }}
    >
      <style>{`
        @keyframes dcFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dcUp { from { transform: translateY(100%); opacity: .6 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          background: T.bg,
          border: `1px solid ${T.line}`, borderBottom: 'none',
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -24px 70px rgba(0,0,0,0.35)',
          animation: open ? 'dcUp 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
          transform: open ? 'none' : 'translateY(100%)',
          transition: open ? 'none' : 'transform 240ms ease',
        }}
      >
        {/* Grabber */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4 }}>
          <div style={{ width: 38, height: 5, borderRadius: 999, background: T.line }} />
        </div>

        <div style={{ padding: '8px 24px 30px', overflowY: 'auto', color: T.text, fontFamily: F.family }}>
          {/* Header 1:1 nach Mock: ruhiger Sub (kein Uppercase/roter Strich),
              große Headline, Lead, durchgehende Hairline. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.sub, marginBottom: 6 }}>{t('dc_kicker')}</div>
              <h2 style={{ margin: 0, fontFamily: F.display, fontSize: 30, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.02, color: T.text }}>{t('dc_title')}</h2>
            </div>
            <OverlayCloseBtn onClick={onClose} />
          </div>
          <div style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.55, marginTop: 12, maxWidth: 470 }}>{t('dc_sub')}</div>
          <div style={{ height: 1, background: T.line, margin: '20px 0 0' }} />

          {/* Hero */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, margin: '22px 0 4px', flexWrap: 'wrap' }}>
            {allClear ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: `2px solid ${T.gain || T.green}`,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.gain || T.green} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                </span>
                <span style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em' }}>{t('dc_all_clear')}</span>
              </div>
            ) : (
              <>
                <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 60, fontWeight: 700, letterSpacing: '-0.05em', lineHeight: 0.86, color: T.text }}>
                  {openCount}
                </span>
                <span style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.4, paddingTop: 8 }}>
                  <b style={{ color: T.text, fontWeight: 700 }}>{t('dc_to_clarify')}</b><br />{t('dc_rest_clear')}
                </span>
              </>
            )}
            <span style={{ marginLeft: 'auto', textAlign: 'right', paddingTop: 6 }}>
              <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, display: 'block', fontSize: 18, fontWeight: 700, color: T.gain || T.green }}>
                {connectedCount} / {totalSources}
              </span>
              <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: T.mute, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>
                {t('dc_sources_live')}
              </span>
            </span>
          </div>

          {/* Zu klären */}
          {!allClear && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.mute, margin: '26px 0 4px' }}>
                {t('dc_sect_open')}
              </div>
              {points.map((p) => {
                const numS = { ...MONO_FEAT, fontFamily: F.num || F.mono, color: T.text, fontWeight: 600 };
                const faint = { color: T.faint || T.mute };
                if (p.kind === 'open_transfer') {
                  const coins = [...new Set(p.items.map(i => i.coin))].slice(0, 4);
                  return (
                    <PointRow key="ot" T={T} dot={'#EAB308'}
                      title={p.count === 1 ? t('dc_open_transfer_one') : t('dc_open_transfer', { n: p.count })}
                      sub={<>{coins.map((c, i) => <React.Fragment key={c}>{i > 0 && ' · '}<span style={numS}>{c}</span></React.Fragment>)} <span style={faint}>— {t('dc_open_transfer_hint')}</span></>}
                      onClick={goFix} />
                  );
                }
                if (p.kind === 'external_receive') {
                  const coins = [...new Set(p.items.map(i => i.coin))].slice(0, 4);
                  return (
                    <PointRow key="er" T={T} dot={'#EAB308'}
                      title={p.count === 1 ? t('dc_external_one') : t('dc_external', { n: p.count })}
                      sub={<>{coins.map((c, i) => <React.Fragment key={c}>{i > 0 && ' · '}<span style={numS}>{c}</span></React.Fragment>)} <span style={faint}>— {t('dc_external_hint')}</span></>}
                      onClick={goFix} />
                  );
                }
                if (p.kind === 'no_basis') {
                  const f = p.items[0];
                  return (
                    <PointRow key="nb" T={T} dot={'#EAB308'}
                      title={p.count === 1 ? t('dc_no_basis_one') : t('dc_no_basis', { n: p.count })}
                      sub={f ? <><span style={numS}>{fmtC(f.menge)} {f.coin}</span> {t('dc_no_basis_from')} <span style={numS}>{fmtDateShort(f.datum, locale)}</span> <span style={faint}>— {t('dc_no_basis_hint')}</span></> : null}
                      onClick={goFix} />
                  );
                }
                return null;
              })}
            </>
          )}

          {/* Quellen */}
          {sources.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.mute, margin: '28px 0 4px' }}>
                {t('dc_sect_sources')}
              </div>
              {sourceGroups.map((g) => {
                const multi = g.entries.length > 1;
                return (
                  <div key={g.logoName} style={{ display: 'flex', alignItems: multi ? 'flex-start' : 'center', gap: 13, padding: '14px 0', borderBottom: `1px solid ${T.lineSoft || T.line}` }}>
                    <span style={{ flexShrink: 0, display: 'inline-flex', paddingTop: multi ? 1 : 0 }}><ExchangeLogo name={g.logoName} size={30} /></span>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: multi ? 12 : 0 }}>
                      {g.entries.map((s) => (
                        <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{s.name}</span>
                            <span style={{ display: 'block', fontSize: 11.5, color: T.faint || T.mute, marginTop: 1 }}>{srcDesc(s)}</span>
                          </span>
                          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: T.sub }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: srcDot(s.kind), flexShrink: 0 }} />
                            {srcStatus(s.kind)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Scam-Dust (informativ) */}
          {dustCount > 0 && (
            <div style={{ fontSize: 12, color: T.mute, marginTop: 16, lineHeight: 1.5 }}>
              {t('dc_dust', { n: dustCount })}
            </div>
          )}

          {/* Positionierung */}
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${T.line}`, fontSize: 12, color: T.sub, lineHeight: 1.6 }}>
            {t('dc_note')}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
